import { useState, useCallback } from 'react';
import { analyzeFile } from '../api/General/attachment';
import { FileAnalysisResult } from '../models/general';

interface UseOcrAnalysisReturn {
  analyzing: boolean;
  result: FileAnalysisResult | null;
  error: string | null;
  analyze: (file: File) => Promise<FileAnalysisResult | null>;
  reset: () => void;
}

export function useOcrAnalysis(): UseOcrAnalysisReturn {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<FileAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (file: File): Promise<FileAnalysisResult | null> => {
    setAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const response = await analyzeFile(file);
      if (response.errors && response.errors.length > 0) {
        setError(response.errors[0]?.message || 'Analysis failed');
        setAnalyzing(false);
        return null;
      }

      const analysisResult = response.payload;
      setResult(analysisResult);
      setAnalyzing(false);
      return analysisResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'OCR analysis failed';
      setError(message);
      setAnalyzing(false);
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setAnalyzing(false);
    setResult(null);
    setError(null);
  }, []);

  return { analyzing, result, error, analyze, reset };
}
